
import React from "react";
import { Job } from "@/types/job";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BookmarkPlus, Bookmark, ArrowUpRight, Clock, MapPin, Building, MessageCircle, Briefcase, PanelTopOpen } from "lucide-react";
import { JobMatchScore } from "./JobMatchScore";

export interface AIJobRecommendationProps {
  job: Job;
  isPremium: boolean;
  isSaved: boolean;
  onSave: () => void;
  showActions?: boolean;
  onViewDetails?: () => void;
}

export const AIJobRecommendation: React.FC<AIJobRecommendationProps> = ({
  job,
  isPremium,
  isSaved,
  onSave,
  showActions = false,
  onViewDetails
}) => {
  const getTimeAgo = (dateString: string) => {
    const postedDate = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - postedDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays < 1) return "Today";
    if (diffInDays === 1) return "Yesterday";
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return `${Math.floor(diffInDays / 30)} months ago`;
  };

  const companyInitials = job.company
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);

  return (
    <Card className="overflow-hidden transition-shadow hover:shadow-md relative">
      {job.matchScore !== undefined && (
        <div className="absolute top-3 right-3 z-10">
          <JobMatchScore score={job.matchScore} isPremium={isPremium} />
        </div>
      )}
      
      <CardHeader className="pb-2">
        <div className="flex gap-4">
          <Avatar className="h-12 w-12 rounded-md">
            <AvatarImage src={job.companyLogo} alt={job.company} />
            <AvatarFallback className="rounded-md bg-primary/10 text-primary">
              {companyInitials}
            </AvatarFallback>
          </Avatar>
          
          <div className="space-y-1 flex-1">
            <CardTitle className="text-xl">{job.title}</CardTitle>
            <CardDescription className="flex items-center gap-2">
              <Building className="h-3.5 w-3.5" />
              {job.company}
              
              {job.remote && (
                <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-200">
                  Remote
                </Badge>
              )}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pb-3">
        <div className="grid grid-cols-2 gap-y-2 text-sm text-gray-500 mb-3">
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" />
            <span>{job.location}</span>
          </div>
          
          <div className="flex items-center gap-1.5">
            <PanelTopOpen className="h-3.5 w-3.5" />
            <span>{job.experience || "All Levels"}</span>
          </div>
          
          <div className="flex items-center gap-1.5">
            <Briefcase className="h-3.5 w-3.5" />
            <span>{job.type || job.jobType || "Full-time"}</span>
          </div>
          
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            <span>{job.postedDate ? getTimeAgo(job.postedDate) : getTimeAgo(job.posted)}</span>
          </div>
        </div>
        
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
          {job.description}
        </p>
        
        <div className="flex flex-wrap gap-1.5">
          {job.requirements && job.requirements.slice(0, 4).map((req, index) => (
            <Badge key={index} variant="secondary" className="bg-gray-100 text-gray-700 font-normal">
              {req}
            </Badge>
          ))}
          {job.requirements && job.requirements.length > 4 && (
            <Badge variant="secondary" className="bg-gray-100 text-gray-700 font-normal">
              +{job.requirements.length - 4} more
            </Badge>
          )}
        </div>
      </CardContent>
      
      <CardFooter className="pt-2">
        <div className="flex justify-between items-center w-full">
          <div className="text-sm font-medium text-gray-800">
            {job.salary}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onSave}>
              {isSaved ? (
                <Bookmark className="h-4 w-4 fill-primary text-primary" />
              ) : (
                <BookmarkPlus className="h-4 w-4" />
              )}
            </Button>
            
            {showActions ? (
              <Button size="sm" className="gap-1" onClick={onViewDetails}>
                <MessageCircle className="h-4 w-4" />
                Apply
              </Button>
            ) : (
              <Button variant="default" size="sm" className="gap-1" onClick={onViewDetails}>
                View Details
                <ArrowUpRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};
